import { useRef, type CSSProperties, type PointerEvent, type ReactNode } from 'react'
import { useReducedMotion } from '../../hooks/useReducedMotion'
import type { ActivityIcon as ActivityName } from '../../lib/activityIcons'
import { ActivityIcon, Glyph } from '../ui/Icon'
import { Faces } from '../ui/Faces'
import { SmartLink } from '../ui/SmartLink'
import './Hero.css'

interface CardProps {
  icon: ActivityName; title: string; meta: string; coord: string; depth: number; tilt: number
  pos: CSSProperties; amber?: boolean; children: ReactNode
}

function PlanCard({ icon, title, meta, coord, depth, tilt, pos, amber, children }: CardProps) {
  return (
    <article className={`pcard glass${amber ? ' amber' : ''}`} data-depth={depth} data-tilt={tilt} style={{ ...pos, transform: `rotate(${tilt}deg)` }}>
      <div className="top">
        <span className="ico"><ActivityIcon name={icon} /></span>
        <div><h3>{title}</h3><div className="meta">{meta}</div></div>
      </div>
      {children}
      <div className="coord">{coord}</div>
    </article>
  )
}

function Seats({ filled, open, total }: { filled: number; open: number; total: number }) {
  return (
    <div className="seats" aria-label={`${filled} of ${total} spots taken`}>
      {Array.from({ length: total }, (_, i) => <b key={i} className={i < filled ? 'f' : i < filled + open ? 'o' : undefined} />)}
    </div>
  )
}

export function Hero() {
  const stageRef = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()

  // The plan cards tilt toward the cursor, each at its own depth.
  const onMove = (e: PointerEvent<HTMLElement>) => {
    if (reduced || e.pointerType !== 'mouse' || !stageRef.current) return
    const nx = e.clientX / window.innerWidth - 0.5
    const ny = e.clientY / window.innerHeight - 0.5
    stageRef.current.querySelectorAll<HTMLElement>('.pcard').forEach((c) => {
      const d = Number(c.dataset.depth) || 1
      const t = Number(c.dataset.tilt) || 0
      c.style.transform = `rotate(${t}deg) translate3d(${(nx * 22 * d).toFixed(1)}px, ${(ny * 18 * d).toFixed(1)}px, 0) rotateY(${(nx * 8).toFixed(1)}deg) rotateX(${(-ny * 8).toFixed(1)}deg)`
    })
  }
  const onLeave = () => {
    stageRef.current?.querySelectorAll<HTMLElement>('.pcard').forEach((c) => { c.style.transform = `rotate(${Number(c.dataset.tilt) || 0}deg)` })
  }

  return (
    <section className="hero" id="top" onPointerMove={onMove} onPointerLeave={onLeave}>
      <div className="wrap">
        <div>
          <span className="live glass thin"><span className="dot" /><span className="neon-tag">Starting in Delhi NCR</span></span>
          <h1>Plans happening <em className="s">near you.</em></h1>
          <p className="lead">mauj puts every game, jam, ride and dinner around you on one live map. Tap a plan, ask to join, show up.</p>
          <div className="ctas">
            <SmartLink className="btn gold" to="/#waitlist">Join the waitlist</SmartLink>
            <SmartLink className="btn clear" to="/#explore">Explore the map <Glyph name="arrow" width={2} /></SmartLink>
          </div>
          <div className="soon">
            <span className="store glass thin"><Glyph name="phone" width={1.7} /><span><small>Coming soon</small>iOS</span></span>
            <span className="store glass thin"><Glyph name="android" width={1.7} /><span><small>Coming soon</small>Android</span></span>
            <span>Starting across India.</span>
          </div>
        </div>
        <div className="stage" ref={stageRef} aria-label="Example plans near Hauz Khas">
          <PlanCard icon="badminton" title="Badminton doubles" meta="Tonight, 8:00 PM · Smash Arena" coord="28.4520° N, 77.0970° E" depth={1.2} tilt={-4} pos={{ left: 0, top: 0 }}>
            <Seats filled={3} open={1} total={4} />
            <div className="foot"><Faces names={['A', 'R', 'K']} /><span>3 in, <span className="ask">1 open</span></span></div>
          </PlanCard>
          <PlanCard icon="guitar" title="Acoustic jam night" meta="7:30 PM · The Chai Adda" coord="28.5540° N, 77.1950° E" depth={2} tilt={3} pos={{ right: 0, top: '31%' }} amber>
            <div className="chips"><span className="chip wf">Women-first</span><span className="chip">Cajón open</span><span className="chip">Free</span></div>
            <div className="foot"><Faces names={['M', 'T', 'N', 'S']} /><span><span className="ask">2 spots</span> left</span></div>
          </PlanCard>
          <PlanCard icon="momo" title="Momo crawl" meta="8:00 PM · Lajpat Nagar" coord="28.5677° N, 77.2433° E" depth={0.7} tilt={-1.5} pos={{ left: '8%', bottom: 0 }}>
            <Seats filled={2} open={1} total={5} />
            <div className="foot"><Faces names={['S', 'D']} /><span>Host reliability <b>98</b></span></div>
          </PlanCard>
          <p className="stage-note">Example plans</p>
        </div>
      </div>
    </section>
  )
}
